import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { CalendarClock, Trash2, Sparkles, Play } from "lucide-react";
import { toast } from "sonner";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export default function EstateSchedules({ properties, employees, isManager }) {
  const [templates, setTemplates] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [propertyId, setPropertyId] = useState("");
  const [assignee, setAssignee] = useState("");

  const load = () => api.get("/estate/schedules").then(({ data }) => setSchedules(data));
  useEffect(() => {
    api.get("/estate/templates").then(({ data }) => setTemplates(data.templates));
    load();
  }, []);
  useEffect(() => { if (!propertyId && properties.length) setPropertyId(properties[0].id); }, [properties]); // eslint-disable-line react-hooks/exhaustive-deps

  const apply = async (t) => {
    if (!propertyId) { toast.error("Pick a property first"); return; }
    try {
      await api.post("/estate/schedules", { property_id: propertyId, title: t.title, category: t.category, frequency: t.frequency, months: t.months, note: t.note, assigned_to_id: assignee || null });
      toast.success(`Added: ${t.title}`);
      load();
    } catch (e) { toast.error("Could not add schedule"); }
  };

  const remove = async (s) => { await api.delete(`/estate/schedules/${s.id}`); load(); };

  const generate = async () => {
    try {
      const { data } = await api.post("/estate/schedules/generate?days=14");
      toast.success(`${data.created} task${data.created === 1 ? "" : "s"} generated for the next 2 weeks`);
    } catch (e) { toast.error("Generation failed"); }
  };

  const byCat = templates.reduce((acc, t) => { (acc[t.category] = acc[t.category] || []).push(t); return acc; }, {});

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="estate-schedules">
      <div className="space-y-3">
        <div className="text-xs font-bold uppercase text-stone-500 flex items-center gap-2"><Sparkles size={14} /> Best-practice library — tap to add</div>
        {isManager && (
          <div className="flex flex-wrap gap-2">
            <select data-testid="sched-property" value={propertyId} onChange={(e) => setPropertyId(e.target.value)} className="h-10 px-2 border border-stone-300 text-sm bg-white font-semibold">
              {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <select data-testid="sched-assignee" value={assignee} onChange={(e) => setAssignee(e.target.value)} className="h-10 px-2 border border-stone-300 text-sm bg-white">
              <option value="">Default crew (assign later)</option>
              {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
            </select>
          </div>
        )}
        <div className="space-y-3 max-h-[560px] overflow-y-auto pr-1">
          {Object.entries(byCat).map(([cat, items]) => (
            <div key={cat} className="bg-white border border-stone-200">
              <div className="px-3 py-2 border-b border-stone-100 text-xs font-bold uppercase text-[#1F3D34]">{cat}</div>
              <div className="divide-y divide-stone-50">
                {items.map((t) => (
                  <button key={t.title} type="button" disabled={!isManager} data-testid={`template-${t.title.replace(/\s+/g, "-")}`} onClick={() => apply(t)}
                    className="w-full text-left px-3 py-2 hover:bg-stone-50 disabled:cursor-default">
                    <div className="text-sm font-semibold">{t.title} <span className="text-[10px] font-bold uppercase text-[#7C9673]">{t.frequency}</span></div>
                    <div className="text-[11px] text-stone-500">{t.note} · {t.months.map((m) => MONTHS[m - 1]).join(", ")}</div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="text-xs font-bold uppercase text-stone-500 flex items-center gap-2"><CalendarClock size={14} /> Active schedules ({schedules.length})</div>
          {isManager && (
            <button data-testid="generate-tasks-btn" onClick={generate}
              className="h-10 px-4 bg-[#1F3D34] text-white font-bold uppercase text-xs flex items-center gap-2">
              <Play size={13} /> Generate upcoming tasks
            </button>
          )}
        </div>
        <div className="space-y-2">
          {schedules.map((s) => (
            <div key={s.id} className="bg-white border border-stone-200 p-3" data-testid={`schedule-${s.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-sm font-bold">{s.title} <span className="text-[10px] font-bold uppercase text-[#7C9673]">{s.frequency}</span></div>
                  <div className="text-[11px] text-stone-500">{s.property_name} · {s.category} · {(s.months || []).map((m) => MONTHS[m - 1]).join(", ")}{s.assigned_to ? ` · → ${s.assigned_to}` : ""}</div>
                </div>
                {isManager && <button data-testid={`delete-schedule-${s.id}`} onClick={() => remove(s)} className="text-stone-300 hover:text-red-500"><Trash2 size={14} /></button>}
              </div>
            </div>
          ))}
          {schedules.length === 0 && (
            <div className="bg-white border border-dashed border-stone-300 p-8 text-center text-stone-400 text-sm" data-testid="schedules-empty">
              No schedules yet — pick a property and tap templates from the library
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
